import { formatTimer, ReactIcons, Spacer, TopBar, useThemeContext } from "@kalyzee/kalyzee-ui";
import React, { useMemo } from "react";
import { UserAvatar } from "@kalyzee/kast-web-sdk/web-components";
import { getNormalUser } from "../../helpers/user";

import styles from "./BaseTopBar.module.css";
import { UserOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { User } from "@kalyzee/kast-web-sdk/websocket";
import { RecordIndicator } from "../utils/RecordIndicator";
import { MainClockRenderer } from "../renderers/mainClock";

export interface BaseTopBarProps {
  user?: User | UserOutputDTO;
  meetingName?: string;
  meetingStartedAt?: string;
  showDate?: boolean;
  showLogo?: boolean;
  watchingNumber?: number;
  settingsBtnRef?: React.Ref<HTMLDivElement>;
  onBack?: () => void;
  onSettings?: () => void;
}

const _BaseTopBar: React.FC<BaseTopBarProps> = ({
  user,
  meetingName,
  meetingStartedAt,
  showDate,
  showLogo,
  watchingNumber,
  settingsBtnRef,
  onBack,
  onSettings,
}) => {
  const themeCtx = useThemeContext();

  const meetingStartedAtDate = useMemo(() => {
    return meetingStartedAt ? new Date(meetingStartedAt) : undefined;
  }, [meetingStartedAt]);

  const left = useMemo(() => {
    const items: React.ReactNode[] = [];
    // Optional : Back
    if (onBack) {
      items.push(
        <ReactIcons.ArrowBack 
          className={styles.backArrow}
          onClick={onBack}
        />
      );
    }

    // Optional : Settings
    if (onSettings) {
      if (items.length) items.push(<Spacer />);
      items.push(
        <div ref={settingsBtnRef}>
          <ReactIcons.Settings
            className={styles.settings}
            onClick={onSettings}
          /> 
        </div>
      );
    }

    // Optional : User
    if (user) {
      const { displayName, avatar } = getNormalUser(user);
      if (items.length) items.push(<Spacer />);
      items.push(
        <UserAvatar
          style={{ width: 26, height: 26 }}
          mode="image"
          image={avatar}
        />
      );
      items.push(<Spacer />);
      items.push(<span className={styles.userName}>{displayName}</span>);
    }

    return items;
  }, [user, onBack, themeCtx.theme, themeCtx.setPreferredTheme]);

  const right = useMemo(() => {
    const items: React.ReactNode[] = [];

    if (meetingName || meetingStartedAtDate) {
      items.push(
        <div className={styles.meetingData}>
          {meetingName && (
            // Optional : Meeting name
            <div className={styles.meetingName}> 
              {meetingName}
            </div>
          )}
          {meetingStartedAtDate && (
            // Optional : Meeting start time
            <div className={styles.meetingMetrics}>
              <RecordIndicator />
              <div className={styles.elapsedTime}>
                <MainClockRenderer
                  render={_ => formatTimer(meetingStartedAtDate)}
                />
              </div>
            </div>
          )}
        </div>
      );
    }

    // Optional : Date
    if (showDate) {
      if (items.length) items.push(<Spacer />);
      items.push(
        <MainClockRenderer
          render={date => {
            let dateString = date.toLocaleDateString(undefined, {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            });
            dateString = dateString.charAt(0).toUpperCase() + dateString.slice(1);
            let timeString = date.toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            });
            return (
              <span className={styles.date}>{dateString} - {timeString}</span>
            );
          }}
        />
      );
    }

    if (watchingNumber !== undefined) {
      if (items.length) items.push(<Spacer />);
      items.push(
        <div className={styles.watchers}>
          <ReactIcons.Eye style={{ width: 20 }} />
          <span>{watchingNumber}</span>
        </div>
      );
    }

    return items;
  }, [meetingName, meetingStartedAtDate, showDate, watchingNumber]);

  const center = useMemo(() => {
    const items: React.ReactNode[] = [];
    // Optional : Logo
    if (showLogo) {
      items.push(
        <ReactIcons.LogoFullAnimated style={{ width: 85, height: 24 }} />,
      );
    }
    return items;
  }, [showLogo]);

  return (
    <TopBar
      left={left}
      right={right}
      center={center}
      className={styles.container}
    />
  );
}

export const BaseTopBar = React.memo(_BaseTopBar);